import { useEffect, useState } from "react";
import api from "@/lib/api";
import { MessageCircleQuestion, Users, TrendingUp } from "lucide-react";

const fmtDate = (iso) => (iso || "").slice(0, 16).replace("T", " ");

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="border hairline bg-white p-5 flex items-center gap-4" data-testid={testid}>
    <Icon size={20} className="text-[var(--brand-primary)]" />
    <div>
      <div className="font-display text-3xl leading-none">{value}</div>
      <div className="label-eyebrow mt-1">{label}</div>
    </div>
  </div>
);

export default function BotAnalyticsTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = () => {
    setError(null);
    api.get("/admin/bot-analytics").then(r => setData(r.data)).catch(e => setError(e.response?.data?.detail || e.message));
  };
  useEffect(load, []);

  if (error) return (
    <div className="border hairline bg-white p-5 text-sm" data-testid="bot-analytics-error">
      <span className="text-[#8B2E2E]">Could not load bot analytics: {String(error)}</span>
      <button className="btn-ghost text-xs ml-3" onClick={load} data-testid="bot-analytics-retry">Retry</button>
    </div>
  );
  if (!data) return <div className="text-sm text-neutral-500" data-testid="bot-analytics-loading">Loading bot analytics…</div>;

  return (
    <div className="space-y-8" data-testid="bot-analytics-tab">
      <div className="grid sm:grid-cols-3 gap-4">
        <Stat icon={MessageCircleQuestion} label="Questions asked" value={data.total_questions} testid="bot-stat-total" />
        <Stat icon={TrendingUp} label="This week" value={data.questions_7d} testid="bot-stat-week" />
        <Stat icon={Users} label="Trainees using the bot" value={data.unique_users} testid="bot-stat-users" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="border hairline bg-white">
          <div className="p-4 border-b hairline label-eyebrow">Most-asked questions</div>
          {data.top_questions.length === 0 ? (
            <div className="p-4 text-sm text-neutral-500" data-testid="bot-top-questions-empty">No questions yet.</div>
          ) : (
            <table className="w-full text-sm" data-testid="bot-top-questions-table">
              <thead className="bg-[color:var(--brand-surface2)]">
                <tr className="text-left label-eyebrow">
                  <th className="p-3">Question</th>
                  <th className="p-3">Times</th>
                  <th className="p-3">Trainees</th>
                  <th className="p-3">Last asked</th>
                </tr>
              </thead>
              <tbody>
                {data.top_questions.map((q, i) => (
                  <tr key={i} className="border-t hairline" data-testid={`bot-top-question-${i}`}>
                    <td className="p-3">{q.question}</td>
                    <td className="p-3 font-display">{q.count}</td>
                    <td className="p-3 text-neutral-600">{q.users}</td>
                    <td className="p-3 font-mono text-xs text-neutral-500">{fmtDate(q.last_asked)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="border hairline bg-white">
          <div className="p-4 border-b hairline label-eyebrow">Curriculum topics trainees struggle with</div>
          {data.top_sources.length === 0 ? (
            <div className="p-4 text-sm text-neutral-500" data-testid="bot-top-sources-empty">No citations yet.</div>
          ) : (
            <div className="p-4 space-y-2" data-testid="bot-top-sources-list">
              {data.top_sources.map((s, i) => {
                const max = data.top_sources[0].citations || 1;
                return (
                  <div key={i} data-testid={`bot-top-source-${i}`}>
                    <div className="flex justify-between text-sm">
                      <span className="truncate pr-3">{s.source}</span>
                      <span className="font-display">{s.citations}</span>
                    </div>
                    <div className="h-1.5 bg-[color:var(--brand-surface2)] mt-1">
                      <div className="h-full bg-[color:var(--brand-primary)]" style={{ width: `${(s.citations / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="border hairline bg-white">
        <div className="p-4 border-b hairline label-eyebrow">Recent questions</div>
        {data.recent.length === 0 ? (
          <div className="p-4 text-sm text-neutral-500" data-testid="bot-recent-empty">Nothing yet.</div>
        ) : (
          <div className="divide-y hairline" data-testid="bot-recent-list">
            {data.recent.map((r, i) => (
              <div key={i} className="p-3 flex flex-wrap items-baseline justify-between gap-2" data-testid={`bot-recent-${i}`}>
                <span className="text-sm">{r.question}</span>
                <span className="font-mono text-xs text-neutral-500">{r.user} · {fmtDate(r.asked_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
